import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Title, Button, FlexBox, Card, CardHeader, Text, Label, MessageStrip, BusyIndicator, Icon, Tag } from '@ui5/webcomponents-react';
import { api } from '../../api/client';

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <FlexBox direction="Column" style={{ minWidth: '7rem' }}><Text style={{ fontSize: '1.5rem', fontWeight: 700 }}>{value}</Text><Label>{label}</Label></FlexBox>
);

/**
 * System / metadata sync. When an admin adds UDFs/UDTs/UDOs in SAP, click "Sync from SAP" so they appear
 * in the designer palette and become usable by generic screens. (Data reads/writes already self-heal.)
 */
export function SystemAdmin() {
  const qc = useQueryClient();
  const info = useQuery({ queryKey: ['meta-info'], queryFn: api.metaInfo });
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative'; text: string } | null>(null);
  const [busy, setBusy] = useState<'sync' | 'seed' | 'generate' | null>(null);
  const cov = useQuery({ queryKey: ['screen-coverage'], queryFn: api.screenCoverage });
  const [showCov, setShowCov] = useState(false);

  const sync = async () => {
    setBusy('sync'); setMsg(null);
    try {
      const r = await api.refreshMeta();
      await qc.invalidateQueries({ queryKey: ['meta-info'] });
      qc.invalidateQueries({ queryKey: ['entities-summary'] });
      qc.invalidateQueries({ queryKey: ['meta'] }); // per-entity metadata used by designer/renderers
      qc.invalidateQueries({ queryKey: ['screen-coverage'] });
      setMsg({ design: 'Positive', text: `Synced ${r.entities} entities from SAP. New UDFs/UDTs/UDOs are now available in the designer.` });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(null); }
  };
  const generate = async () => {
    const n = cov.data?.missing.length ?? 0;
    if (!n || !window.confirm(`Create ${n} starter screen(s) for the Service Layer entity sets that have none? They are read-only by default; refine them in the Designer. Existing screens are not touched.`)) return;
    setBusy('generate'); setMsg(null);
    try {
      const r = await api.generateScreens();
      await qc.invalidateQueries({ queryKey: ['screen-coverage'] });
      qc.invalidateQueries({ queryKey: ['screens'] }); qc.invalidateQueries({ queryKey: ['screens-all'] });
      setMsg({ design: r.failed.length ? 'Negative' : 'Positive', text: `Created ${r.created.length} screen(s).${r.failed.length ? ` ${r.failed.length} failed: ${r.failed.map(f => `${f.entity} (${f.error})`).join('; ')}` : ''}` });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(null); }
  };
  const reseed = async () => {
    if (!window.confirm('Re-create the default screens (business-partners, items, sales-orders, activities)? Existing ones are left untouched unless you choose to overwrite.')) return;
    setBusy('seed'); setMsg(null);
    try {
      const r = await api.seedScreens(false);
      qc.invalidateQueries({ queryKey: ['screens'] }); qc.invalidateQueries({ queryKey: ['screens-all'] });
      setMsg({ design: 'Positive', text: r.seeded.length ? `Seeded: ${r.seeded.join(', ')}` : 'All default screens already exist.' });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(null); }
  };

  return (
    <div>
      <Title level="H3" style={{ marginBottom: '0.75rem' }}>System</Title>
      {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.75rem' }}>{msg.text}</MessageStrip>}

      <Card header={<CardHeader titleText="SAP metadata" subtitleText="Fields, tables and objects discovered from Service Layer" avatar={<Icon name="synchronize" />} />} style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1rem' }}>
          {info.isLoading ? <BusyIndicator active /> : info.data ? (
            <>
              <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginBottom: '1rem' }}>
                <Stat label="Entities" value={info.data.entities} />
                <Stat label="User tables (UDT)" value={info.data.udts} />
                <Stat label="User objects (UDO)" value={info.data.udos} />
                <Stat label="UDF fields" value={info.data.udfFields} />
                <Stat label="Enums" value={info.data.enums} />
              </FlexBox>
              <Text style={{ color: 'var(--sapNeutralTextColor)' }}>Last synced: {new Date(info.data.loadedAt).toLocaleString()}</Text>
            </>
          ) : <Text>Metadata not loaded.</Text>}
        </div>
      </Card>

      <Card header={<CardHeader titleText="Service Layer coverage" subtitleText="Every entity set SAP exposes should have a screen" avatar={<Icon name="screen-split-two" />} />} style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1rem' }}>
          {cov.isLoading ? <BusyIndicator active /> : cov.error ? <MessageStrip design="Negative" hideCloseButton>{(cov.error as Error).message}</MessageStrip> : cov.data && (<>
            <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginBottom: '0.75rem' }}>
              <Stat label="Entity sets" value={cov.data.entities} />
              <Stat label="With a screen" value={cov.data.covered} />
              <Stat label="Screens" value={cov.data.screens} />
              <Stat label="Missing" value={cov.data.missing.length} />
              <Stat label="Not screens (internal)" value={cov.data.excluded.length} />
            </FlexBox>
            {cov.data.missing.length === 0
              ? <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '0.5rem' }}>All business entity sets exposed by Service Layer have a screen.</MessageStrip>
              : <MessageStrip design="Critical" hideCloseButton style={{ marginBottom: '0.5rem' }}>{cov.data.missing.length} entity set(s) have no screen yet — typically added by an SAP upgrade or a new UDO. Generate starter screens, then refine them in the Designer.</MessageStrip>}
            <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
              {cov.data.missing.length > 0 && <Button design="Emphasized" icon="add-document" disabled={busy !== null} onClick={generate}>{busy === 'generate' ? 'Generating…' : `Generate ${cov.data.missing.length} missing screen(s)`}</Button>}
              <Button design="Transparent" icon={showCov ? 'collapse' : 'expand'} onClick={() => setShowCov(v => !v)}>{showCov ? 'Hide details' : 'Show details'}</Button>
            </FlexBox>
            {showCov && <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem' }}>
              {cov.data.missing.length > 0 && <><Label>Missing</Label>
                <FlexBox wrap="Wrap" style={{ gap: '0.3rem', margin: '0.25rem 0 0.75rem' }}>{cov.data.missing.map(m => <Tag key={m.set} colorScheme="1" title={`${m.group}${m.subgroup ? ` / ${m.subgroup}` : ''}`}>{m.set}</Tag>)}</FlexBox></>}
              <Label>Deliberately not screens</Label>
              <FlexBox wrap="Wrap" style={{ gap: '0.3rem', marginTop: '0.25rem' }}>{cov.data.excluded.map(x => <Tag key={x.set} colorScheme="10" title={x.reason}>{x.set}</Tag>)}</FlexBox>
            </div>}
          </>)}
        </div>
      </Card>

      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.5rem' }}>
        <b>Adding fields over time.</b> 1) Add the UDF / UDT / UDO in SAP (SAP client, DI, or the setup script).
        2) Service Layer must expose it — it runs several worker nodes that each cache metadata, so a new object
        appears reliably only after the <b>Service Layer service is restarted</b> (or its nodes refresh on their own).
        3) Back here, click <b>Sync from SAP</b> (or just restart the B1Links server — it auto-syncs on startup and
        on first access to a new object). Existing screens keep working throughout; data access self-heals automatically.
      </MessageStrip>
      <MessageStrip design="Critical" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        If a newly added field or table doesn’t appear after Sync, restart the SAP Service Layer service, then Sync again.
      </MessageStrip>
      <FlexBox style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button design="Emphasized" icon="synchronize" disabled={busy !== null} onClick={sync}>{busy === 'sync' ? 'Syncing…' : 'Sync from SAP'}</Button>
        <Button icon="add-document" disabled={busy !== null} onClick={reseed}>{busy === 'seed' ? 'Seeding…' : 'Seed default screens'}</Button>
      </FlexBox>
    </div>
  );
}
